"use client";

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

export interface TimelineEventsContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const TimelineEventsContext = createContext<TimelineEventsContextValue | null>(null);

/**
 * Shared open/close state for the Timeline Events (left) panel so the toggle can
 * live on the horizontal timeline axis while the drawer renders via portal.
 * Mirrors the SessionHistory and ToolsDrawer provider patterns.
 */
export function TimelineEventsProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const value = useMemo<TimelineEventsContextValue>(
    () => ({ isOpen, open, close, toggle }),
    [isOpen, open, close, toggle],
  );

  return (
    <TimelineEventsContext.Provider value={value}>
      {children}
    </TimelineEventsContext.Provider>
  );
}

export function useTimelineEvents(): TimelineEventsContextValue | null {
  return useContext(TimelineEventsContext);
}
